import React from 'react';
import { Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text } = Typography;

/** Server-provided display-ready timestamp entry — never client-computed CAL policy. */
export interface TimeContextEntryV0 {
  readonly label: string;
  readonly value: string | null;
}

export interface TimeContextPanelProps {
  readonly presentationState: PrimitivePresentationState;
  readonly entries?: readonly TimeContextEntryV0[];
  /** Server-computed freshness label only — never fabricated client-side. */
  readonly freshnessLabel?: string | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

/**
 * TimeContextPanel (Family B) — groups temporal fields (created/assigned/
 * deadline/completed, etc.) that are otherwise scattered across ad-hoc
 * Descriptions blocks. Does not implement CAL scheduling/eligibility policy;
 * it is a display-only grouping of server-supplied timestamps.
 */
export function TimeContextPanel(props: TimeContextPanelProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('TimeContextPanel must keep grantsAuthority/grantsApproval=false');
  }

  const entries = props.entries ?? [];

  return (
    <PrimitiveSurfaceFrame
      title="시간 컨텍스트"
      presentationState={props.presentationState}
      emptyMessage="표시할 시간 정보가 없습니다."
      data-testid={props['data-testid'] ?? 'time-context-panel'}
      aria-label="시간 컨텍스트"
    >
      <div className={styles.primitiveStack}>
        {props.freshnessLabel ? (
          <Text type="secondary" data-testid="time-context-freshness">
            신선도(서버): {props.freshnessLabel}
          </Text>
        ) : null}
        {entries.length === 0 ? (
          <Text type="secondary">시간 필드가 없습니다.</Text>
        ) : (
          entries.map((entry) => (
            <div key={entry.label} className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>{entry.label}</span>
              <Text>{entry.value?.trim() ? entry.value : '—'}</Text>
            </div>
          ))
        )}
      </div>
    </PrimitiveSurfaceFrame>
  );
}
